import React from "react";

export default function PrivacyPolicy({
  companyName = "Intelisparkz Systems Private Limited",
  websiteUrl = "https://intelisparkz.com",
  lastUpdated = "May 2026",
  contactEmail = "contact@intelisparkz.com",
  phone = "+91 9583393333",
  addressLine1 = "K8 Kalinga Nagar, Shampur, Bhubaneswar, Odisha 751003, India",
  addressLine2 = "",
}) {
  return (
    <section className="container" style={{ padding: "9rem 0" }}>
      {/* Header */}
      <header className="mb-4">
        <h1 className="h2 fw-bold text-dark">Privacy Policy</h1>
        <p className="text-muted small">Last updated: {lastUpdated}</p>
      </header>

      {/* Intro */}
      <div className="mb-4">
        Welcome to <strong>{companyName}</strong> ("we", "us", "our"). We are
        committed to protecting your privacy. This Privacy Policy explains how
        we collect, use, disclose, and safeguard your personal information when
        you visit our website:{" "}
        <a
          href={websiteUrl}
          target="_blank"
          rel="noreferrer"
          className="text-primary text-decoration-none"
        >
          {websiteUrl}
        </a>{" "}
        ("Website"). By using the Website, you consent to the practices
        described here.
      </div>

      {/* Section 1 */}
      <section className="mb-4">
        <h5 className="fw-semibold">1. Information We Collect</h5>
        <ul className="list-unstyled ms-3">
          <li className="mb-2">
            <strong>Personal Information</strong>: name, email address, phone
            number, postal address and any other details you provide via contact
            forms, newsletter sign-ups, or similar interactions.
          </li>
          <li className="mb-2">
            <strong>Technical / Usage Data</strong>: IP address, browser type
            and version, operating system, device identifiers, pages visited,
            time on page, referring URLs, cookie identifiers, and analytics
            data.
          </li>
          <li>
            <strong>Cookies & Tracking Technologies</strong>: used for site
            functionality, performance, and analytics. See the Cookies section
            below.
          </li>
        </ul>
      </section>

      {/* Section 2 */}
      <section className="mb-4">
        <h5 className="fw-semibold">2. How We Use Your Information</h5>
        <ul className="ms-3">
          <li>Respond to inquiries and provide support.</li>
          <li>
            Send newsletters or marketing communications (only if you opt in).
          </li>
          <li>Operate, maintain, personalize, and improve the Website.</li>
          <li>Monitor usage, perform analytics, and ensure security.</li>
          <li>Comply with legal obligations and prevent misuse or fraud.</li>
        </ul>
      </section>

      {/* Section 3 */}
      <section className="mb-4">
        <h5 className="fw-semibold">3. Sharing & Disclosure</h5>
        <p>
          We do not sell your Personal Information. We may share it with trusted
          service providers who assist in operating the Website and delivering
          services, under confidentiality obligations; when required by law; or
          to protect the rights, property, or safety of {companyName}, our
          users, or others.
        </p>
      </section>

      {/* Section 4 */}
      <section className="mb-4">
        <h5 className="fw-semibold">4. Data Retention</h5>
        <p>
          We retain personal data only as long as necessary for the purposes
          described in this policy, unless a longer retention period is required
          or permitted by law.
        </p>
      </section>

      {/* Section 5 */}
      <section className="mb-4">
        <h5 className="fw-semibold">5. Your Rights</h5>
        <p>
          Depending on your location, you may have rights to access, correct,
          delete, or restrict processing of your personal data; to object to
          processing; to withdraw consent where processing is based on consent;
          and to lodge a complaint with a data protection authority.
        </p>
      </section>

      {/* Section 6 */}
      <section className="mb-4">
        <h5 className="fw-semibold">6. Security</h5>
        <p>
          We implement reasonable administrative, technical, and organizational
          measures to protect personal data. However, no method of transmission
          or storage is completely secure, and we cannot guarantee absolute
          security.
        </p>
      </section>

      {/* Section 7 */}
      <section className="mb-4">
        <h5 className="fw-semibold">7. Cookies & Similar Technologies</h5>
        <p>
          We use necessary cookies for site functionality and analytics cookies
          to understand Website usage. You can control cookies via your browser
          settings; disabling some cookies may affect site features. If you
          implement a cookie banner, link it here.
        </p>
      </section>

      {/* Section 8 */}
      <section className="mb-4">
        <h5 className="fw-semibold">8. Children’s Privacy</h5>
        <p>
          The Website is not intended for individuals under the age of 18. We do
          not knowingly collect personal information from minors. If you believe
          a minor has provided personal data, please contact us so we can delete
          it.
        </p>
      </section>

      {/* Section 9 */}
      <section className="mb-4">
        <h5 className="fw-semibold">9. Third-Party Links & Services</h5>
        <p>
          Our Website may link to third-party sites and services. Their privacy
          practices are independent from ours; please review their policies.
        </p>
      </section>

      {/* Section 10 */}
      <section className="mb-4">
        <h5 className="fw-semibold">10. International Data Transfers</h5>
        <p>
          Your information may be transferred to and processed in countries
          other than your own. We take steps to ensure appropriate safeguards
          are in place consistent with applicable laws.
        </p>
      </section>

      {/* Section 11 */}
      <section className="mb-4">
        <h5 className="fw-semibold">11. Changes to this Privacy Policy</h5>
        <p>
          We may update this Policy from time to time. Material changes will be
          communicated by posting a prominent notice on the Website or by other
          appropriate means. The “Last updated” date will indicate when changes
          took effect.
        </p>
      </section>

      {/* Section 12 */}
      <section className="mb-4">
        <h5 className="fw-semibold">12. Contact Us</h5>
        <address className="text-muted">
          <div>
            <strong>{companyName}</strong>
          </div>
          <div>{addressLine1}</div>
          {addressLine2 && <div>{addressLine2}</div>}
          <div>
            Email:{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="text-primary text-decoration-none"
            >
              {contactEmail}
            </a>
            {phone ? <span className="ms-2">| Phone: {phone}</span> : null}
          </div>
        </address>
      </section>

      {/* Footer */}
      <footer className="mt-4 pt-3 border-top text-muted small">
        This policy is based on common best practices and is intended to be
        adapted to your specific operations and applicable laws. Consult legal
        counsel before publishing.
      </footer>
    </section>
  );
}
